/** Inline "Add task" row used under groups in My Tasks and List view. */
import { useState } from 'react';
import { CalendarDays, Flag, Plus, Repeat, Tag as TagIcon } from 'lucide-react';
import { formatDueLabel, todayInTz, type ISODate, type QuickAddToken } from '@karya/core';
import { cn } from '@/lib/cn';
import { useTimeSettings, useWorkspace } from '@/data/queries';
import { Button } from '@/components/ui/button';
import { StatusIcon } from './status';
import { createFromParse, useQuickParse } from './use-quick-parse';

export function TokenChips({
  tokens,
  onDismiss,
}: {
  tokens: QuickAddToken[];
  onDismiss?: (t: QuickAddToken) => void;
}) {
  if (tokens.length === 0) return null;
  const icon = (t: QuickAddToken) =>
    t.type === 'date' || t.type === 'time' ? (
      <CalendarDays className="size-3.5" />
    ) : t.type === 'priority' ? (
      <Flag className="size-3.5" />
    ) : t.type === 'tag' ? (
      <TagIcon className="size-3.5" />
    ) : t.type === 'recurrence' ? (
      <Repeat className="size-3.5" />
    ) : null;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {tokens.map((t) => (
        <span
          key={`${t.start}-${t.type}`}
          className="inline-flex h-6 items-center gap-1 rounded-xs bg-primary-soft px-1.5 text-cap text-primary-ink"
        >
          {icon(t)}
          {t.label}
          {onDismiss && (
            <button
              aria-label={`Keep “${t.text}” as text`}
              onClick={() => onDismiss(t)}
              className="ml-0.5 opacity-70 hover:opacity-100"
            >
              ×
            </button>
          )}
        </span>
      ))}
    </span>
  );
}

export function InlineAdd({
  listId,
  dueDate,
  statusId,
  parentTaskId,
  label = 'Add task',
  phone,
  indent = 0,
  autoOpen = false,
  onCreated,
}: {
  listId: string | null;
  dueDate?: ISODate | null;
  statusId?: string | null;
  parentTaskId?: string | null;
  label?: string;
  phone?: boolean;
  indent?: number;
  autoOpen?: boolean;
  onCreated?: () => void;
}) {
  const [open, setOpen] = useState(autoOpen);
  const [text, setText] = useState('');
  const [ignored, setIgnored] = useState<Array<[number, number]>>([]);
  const [now] = useState(() => new Date());
  const ws = useWorkspace();
  const { tz } = useTimeSettings();
  const list = listId ? ws.listById.get(listId) : undefined;
  const parsed = useQuickParse(text, ignored, list?.space_id ?? null, now);
  const status = statusId ? ws.statusById.get(statusId) : undefined;

  const submit = async () => {
    if (!text.trim() || !list) return;
    await createFromParse(parsed, {
      listId: list.id,
      spaceId: list.space_id,
      dueDate,
      statusId,
      parentTaskId,
    });
    setText('');
    setIgnored([]);
    onCreated?.();
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        disabled={!list}
        className={cn(
          'flex w-full items-center gap-2 border-b border-line px-3 text-left text-sm text-ink-3 hover:bg-surface-2 hover:text-ink-2',
          phone ? 'h-11 px-4' : 'h-8',
        )}
        style={{ paddingLeft: (phone ? 16 : 12) + indent }}
      >
        <Plus className="size-3.5" />
        {list ? label : 'Choose a default list in Settings to add tasks here'}
      </button>
    );
  }
  const dueChip =
    !parsed.due_date && dueDate ? formatDueLabel(dueDate, null, todayInTz(tz, now)) : null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 border border-primary bg-surface-1 px-3 py-1.5"
      style={{ paddingLeft: (phone ? 16 : 12) + indent }}
    >
      <StatusIcon status={status} />
      <input
        autoFocus
        value={text}
        aria-label="Task name"
        placeholder="Task name — try “tomorrow 5pm !high #tag”"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') {
            setOpen(false);
            setText('');
          }
        }}
        onBlur={() => !text && setOpen(false)}
        className="h-7 min-w-[180px] flex-1 bg-transparent outline-none placeholder:text-ink-3"
      />
      <TokenChips
        tokens={parsed.tokens}
        onDismiss={(t) => setIgnored((r) => [...r, [t.start, t.end]])}
      />
      {dueChip && (
        <span className="inline-flex h-6 items-center gap-1 rounded-xs bg-surface-2 px-1.5 text-cap text-ink-2">
          <CalendarDays className="size-3.5" />
          {dueChip}
        </span>
      )}
      {list && (
        <span className="text-cap text-ink-3">
          in{' '}
          {parsed.list
            ? `${parsed.list.emoji ?? ''} ${parsed.list.name}`
            : `${list.emoji ?? ''} ${list.name}`}
        </span>
      )}
      <Button
        variant="primary"
        size="sm"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => void submit()}
        disabled={!text.trim()}
      >
        Save
      </Button>
    </div>
  );
}
